import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getSingleVideo } from "./service/featured";
import videojs from "video.js";
import "videojs-playlist";
import "videojs-contrib-ads";
import "videojs-ima";
import "video.js/dist/video-js.css";
import "videojs-ima/dist/videojs.ima.css";
import "bootstrap/dist/css/bootstrap.css";
import vibeStreamLogo from "./assets/new-logo.png";
import codeIcon from "./assets/code-icon.png";
import playIcn from "./assets/play-button.png";
import { copyToClipboard, getVideoMimeType } from "./helpers/globals";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import useVideoAnalytics from "./hooks/useVideoAnalytics";
import { extractVideoId } from "./utils/urlHelpers";

export function CopyEmbedVideo({ video }) {
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const targetVideoId = video?.id;

  function handleCopy(width, height) {
    return () => {
      setIsCopyModalOpen(false);
      copyToClipboard(
        `<iframe width="${width}" height="${height}" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen src="${window.location.protocol}//${window.location.host}/embed/${targetVideoId}"></iframe>`
      );
      setCopied(true);
    };
  }

  function handleCopyPlayer() {
    setIsCopyModalOpen(false);
    copyToClipboard(
      `<iframe width="100%" height="100%" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen src="${window.location.protocol}//${window.location.host}/embed/${targetVideoId}?playerOnly=true"></iframe>`
    );
    setCopied(true);
  }

  return (
    <>
      <img
        src={codeIcon}
        alt="code icon"
        style={{ cursor: "pointer" }}
        onClick={() => setIsCopyModalOpen(true)}
      />
      <Modal
        isOpen={isCopyModalOpen}
        toggle={() => setIsCopyModalOpen(!isCopyModalOpen)}
      >
        <ModalHeader>Embed Video</ModalHeader>
        <ModalBody
          style={{ display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}
        >
          <Button color="primary" onClick={handleCopy(300, 600)}>
            Copy (300x600)
          </Button>
          <Button color="primary" onClick={handleCopy(970, 250)}>
            Copy (970x250)
          </Button>
          <Button color="success" onClick={handleCopyPlayer}>
            Copy Player
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setIsCopyModalOpen(!isCopyModalOpen)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={copied} toggle={() => setCopied(!copied)}>
        <ModalHeader>Embed video copied</ModalHeader>
        <ModalBody>
          The video embed code of "{video?.title}" has been copied to the
          clipboard.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCopied(!copied)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function EmbedVideo() {
  const [video, setVideo] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [embedSize, setEmbedSize] = useState(null);
  const [isMuted, setIsMuted] = useState(true);

  const analytics = useVideoAnalytics();

  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const isPlayerOnly = searchParams.get('playerOnly') === 'true';

  // Initialize Google Analytics for the embed widget
  useEffect(() => {
    if (!document.getElementById("ga-gtag-widget")) {
      const script = document.createElement("script");
      script.id = "ga-gtag-widget";
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-4TJT1EM1C3";
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-4TJT1EM1C3");
    }
  }, []);

  // Detect embed size from window dimensions
  useEffect(() => {
    const detectEmbedSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Determine if it's 300x600 or 970x250
      if (width <= 350 && height >= 550) {
        setEmbedSize('300x600');
      } else if (width >= 900 && height <= 300) {
        setEmbedSize('970x250');
      } else {
        setEmbedSize('default');
      }
    };

    detectEmbedSize();
    window.addEventListener('resize', detectEmbedSize);
    return () => window.removeEventListener('resize', detectEmbedSize);
  }, []);

  useEffect(() => {
    const imaOptions = {
      adTagUrl:
        "https://pubads.g.doubleclick.net/gampad/ads?iu=/22978710279/EVP-1&description_url=https%3A%2F%2Fwww.vibe-stream.tv&tfcd=0&npa=0&sz=640x480&min_ad_duration=0&max_ad_duration=60000&gdfp_req=1&unviewed_position_start=1&output=vast&env=vp&impl=s&correlator=",
    };

    const videoId = extractVideoId(params.videoId || params['*'] || window.location.pathname);

    if (!videoId) return;

    getSingleVideo(videoId)
      .then((response) => {
        const fetchedVideo = response.data.data;
        setVideo(fetchedVideo);

        const videoMeta = {
          src: fetchedVideo.video_url,
          type: getVideoMimeType(fetchedVideo.video_url),
        };

        const videoOptions = {
          controls: true,
          controlBar: {
            pictureInPictureToggle: false,
          },
          muted: true,
        };

        playerRef.current = videojs(videoRef.current, videoOptions);
        playerRef.current.src(videoMeta);
        playerRef.current.muted(true);
        playerRef.current.ima(imaOptions);
        playerRef.current.on("play", () => {
          setIsVideoPlaying(true);
          analytics.start(fetchedVideo.id);
        });
        playerRef.current.on("ended", () => {
          setIsVideoPlaying(false);
          analytics.record();
          playerRef.current?.play?.();
        });
        playerRef.current.on("pause", () => {
          setIsVideoPlaying(false);
          analytics.pause();
        });
        playerRef.current.on("touchstart", (e) => {
          if (e.target.classList.contains("vjs-playing")) {
            // this.bigPlayButton.show();
            analytics.pause();
            playerRef.current.pause();
          } else {
            playerRef.current.play();
          }
        });

        // Autoplay for 300x600 and 970x250 embeds after player is ready
        playerRef.current.ready(() => {
          // Check embed size from window dimensions
          const width = window.innerWidth;
          const height = window.innerHeight;
          const isSmallEmbed = (width <= 350 && height >= 550) || (width >= 900 && height <= 300);

          if (isSmallEmbed) {
            setTimeout(() => {
              playerRef.current.play().catch(err => {
                console.log('Autoplay prevented:', err);
              });
            }, 1000);
          }
        });

        return () => {
          playerRef.current.dispose();
        };
      })
      .catch((error) => console.error(error));
  }, []);

  const handlePausePlay = (e) => {
    e.stopPropagation();
    if (playerRef.current) {
      if (playerRef.current.paused()) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  };

  const handleThumbnailClick = () => {
    if (playerRef.current && playerRef.current.paused()) {
      playerRef.current.play();
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (playerRef.current) {
      const newMutedState = !playerRef.current.muted();
      playerRef.current.muted(newMutedState);
      setIsMuted(newMutedState);
    }
  };

  // Spacebar pause/play handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        handlePausePlay(e);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const showCustomArtwork = useCallback((artwork) => {
    const isArtworkEnabled = video?.widget_customized === true;
    if (isArtworkEnabled) {
      return !!video[artwork];
    }
    return isArtworkEnabled;
  }, [video])

  if (isPlayerOnly) {
    return (
      <div style={{ width: "100%", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        {video && (
          <>
            <video
              ref={videoRef}
              className="video-js vjs-fluid vjs-default-skin vjs-big-play-centered single-video"
              width="100%"
              height="100%"
              preload="auto"
              allowFullScreen
              poster={video?.thumbnail_url}
            />

            {/* Metadata overlay - always visible */}
            <div
              style={{
                position: "absolute",
                top: "20px",
                left: "20px",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                pointerEvents: "none"
              }}
            >
              <img
                src={vibeStreamLogo}
                alt="Vibe Stream Logo"
                style={{
                  width: "60px",
                  height: "60px",
                  filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.5))"
                }}
              />
              <div
                style={{
                  fontFamily: "EagleBold, Arial, sans-serif",
                  color: "white",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                  lineHeight: "1.1"
                }}
              >
                <div style={{ fontSize: "24px" }}>
                  {video?.artist}
                </div>
                <div style={{ fontSize: "20px" }}>
                  '{video?.title}'
                </div>
              </div>
            </div>

            {!isVideoPlaying && (
              <div
                onClick={handleThumbnailClick}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1
                }}
              >
                <img
                  src={playIcn}
                  alt="Play"
                  style={{
                    width: "80px",
                    height: "80px",
                    opacity: 0.9
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // 300x600 layout
  if (embedSize === '300x600') {
    return (
      <div className="embed-container-300x600">
        {video && (
          <>
            {/* Metadata above player */}

            {showCustomArtwork('widget_image_300_A') ? (
              <div className="embed-300x600-custom-artwork-top">
                <img src={video?.widget_image_300_A} alt={`${video?.title} top artwork`} />
              </div>
            ) : (
              <div className="embed-300x600-metadata">
                <img src={vibeStreamLogo} alt="Vibe Stream Logo" className="embed-300x600-logo" />
                <div className="embed-300x600-text">
                  <div className="embed-300x600-artist">{video?.artist}</div>
                  <div className="embed-300x600-title">'{video?.title}'</div>
                </div>
              </div>
            )}

            {/* Video player with custom play button overlay */}
            <div className="embed-300x600-player">
              <video
                ref={videoRef}
                className="video-js vjs-fluid vjs-default-skin vjs-big-play-centered single-video"
                width="100%"
                height="auto"
                preload="auto"
                allowFullScreen
                poster={video?.thumbnail_url}
              />
              {!isVideoPlaying && (
                <div
                  onClick={handleThumbnailClick}
                  className="embed-300x600-play-overlay"
                >
                  <img src={playIcn} alt="Play" className="embed-300x600-play-button" />
                </div>
              )}
              {/* Mute/Unmute button */}
              <button
                onClick={toggleMute}
                className="embed-mute-button"
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? "🔇" : "🔊"}
              </button>
            </div>

            {showCustomArtwork('widget_image_300_B') ? (
              <div className="embed-300x600-custom-artwork-bottom">
                <img src={video?.widget_image_300_B} alt={`${video?.title} bottom artwork`} />
              </div>
            ) : (
              <>
                {/* Centered square album artwork */}
                <div className="embed-300x600-artwork">
                  <img src={video?.album_artwork} alt={video?.title} />
                </div>

                {/* Call to Action */}
                <div className="embed-300x600-cta">
                  Discover & Enjoy New Music<br />
                  On Vibe Stream
                </div>
              </>)}
          </>
        )}
      </div>
    );
  }

  // 970x250 layout
  if (embedSize === '970x250') {
    return (
      <div className="embed-container-970x250">
        {video && (
          <>
            {/* Video player */}
            <div className="embed-970x250-player">
              <video
                ref={videoRef}
                className="video-js vjs-fluid vjs-default-skin vjs-big-play-centered single-video"
                width="100%"
                height="100%"
                preload="auto"
                allowFullScreen
                poster={video?.thumbnail_url}
              />
              {!isVideoPlaying && (
                <div
                  onClick={handleThumbnailClick}
                  className="embed-970x250-play-overlay"
                >
                  <img src={playIcn} alt="Play" className="embed-970x250-play-button" />
                </div>
              )}
              {/* Mute/Unmute button */}
              <button
                onClick={toggleMute}
                className="embed-mute-button"
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? "🔇" : "🔊"}
              </button>
            </div>

            {/* Info area with artist, title, logo, and CTA */}
            {showCustomArtwork('widget_image_970') ? (
              <div className="embed-970x250-custom-artwork">
                <img src={video?.widget_image_970} alt={`${video?.title} artwork`} />
              </div>
            ) : (
              <>
                <div className="embed-970x250-info">
                  <div className="embed-970x250-text">
                    <div className="embed-970x250-artist">{video?.artist}</div>
                    <div className="embed-970x250-title">'{video?.title}'</div>
                  </div>
                  <img src={vibeStreamLogo} alt="Vibe Stream Logo" className="embed-970x250-logo" />
                  <div className="embed-970x250-cta">
                    Discover & Enjoy New Music<br />
                    On Vibe Stream
                  </div>
                </div>

                {/* Square album artwork */}
                <div className="embed-970x250-artwork">
                  <img src={video?.album_artwork} alt={video?.title} />
                </div>
              </>)}
          </>
        )}
      </div>
    );
  }

  // Default layout for other sizes
  return (
    <div className="embed-container">
      {video && (
        <>
          <div className="logo-image-sm">
            <img src={vibeStreamLogo} alt="logo" />
          </div>
          <div className="embed-player">
            <video
              ref={videoRef}
              className="video-js vjs-fluid vjs-default-skin vjs-big-play-centered single-video"
              width="auto"
              height="auto"
              preload="auto"
              allowFullScreen
              poster={video?.thumbnail_url}
            />
          </div>
          <div className="embed-footer">
            <div className="embed-info">
              <img src={vibeStreamLogo} alt="logo" className="logo-image" />
              <img
                src={video?.thumbnail_url}
                alt="thumbnail"
                className="embed-campaign-image"
              />
              <span>
                {video?.title} by {video?.artist}
              </span>
            </div>
            <div className="embed-playlist">
              <img src={video?.album_artwork} alt={video?.title} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EmbedVideo;
